import { createPublicClient, http, createWalletClient, custom, defineChain } from 'viem';
import NFT_ABI from '../assets/abi/NFT.json';
import { parseEther, formatEther } from 'viem';

// 定义Unichain主网配置
export const unichainMainnet = defineChain({
  id: Number(import.meta.env.VITE_CHAIN_ID) || 130,
  name: import.meta.env.VITE_CHAIN_NAME || 'Unichain',
  network: 'unichain',
  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: {
      http: [import.meta.env.VITE_CHAIN_RPC || 'https://unichain-mainnet.infura.io/v3/f3520456e3b141d291363daacc9bb49c'],
    },
    public: {
      http: [import.meta.env.VITE_CHAIN_RPC || 'https://unichain-mainnet.infura.io/v3/f3520456e3b141d291363daacc9bb49c'],
    },
  },
  blockExplorers: {
    default: { name: 'UnichainScan', url: 'https://uniscan.io' },
  },
  testnet: false,
});


// 获取合约地址
const CONTRACT_ADDRESS = import.meta.env.VITE_CONTRACT_ADDRESS;

// 创建公共客户端
const publicClient = createPublicClient({
  chain: unichainMainnet,
  transport: http()
});

// unichainChainIdHex 常量
const unichainChainIdHex = '0x82'; // 130 in hex

/**
 * 验证并切换到正确的网络
 * 注意：此方法仍需要钱包对象，但会在 AuthProvider 初始化钱包后使用
 */
export const verifyAndSwitchNetwork = async (ethereum) => {
  if (!ethereum) {
    throw new Error('未找到以太坊钱包对象');
  }

  try {
    // 尝试切换网络
    try {
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: unichainChainIdHex }]
      });
    } catch (switchError) {
      // 如果网络未添加，则添加网络
      if (switchError.code === 4902) {
        await ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: unichainChainIdHex,
            chainName: import.meta.env.VITE_CHAIN_NAME || 'Unichain',
            nativeCurrency: {
              name: 'ETH',
              symbol: 'ETH',
              decimals: 18
            },
            rpcUrls: [import.meta.env.VITE_CHAIN_RPC || 'https://unichain-mainnet.infura.io/v3/f3520456e3b141d291363daacc9bb49c'],
            blockExplorerUrls: ['https://uniscan.io']
          }]
        });
      } else {
        throw switchError;
      }
    }
    
    // 验证当前网络
    const currentChainId = await ethereum.request({ method: 'eth_chainId' });
    if (currentChainId !== unichainChainIdHex) {
      throw new Error('请确认钱包已切换到Unichain主网');
    }
    
    return true;
  } catch (error) {
    console.error('网络切换失败:', {
      error,
      message: error.message
    });
    throw error;
  }
};

/**
 * 获取铸造费用
 * @returns {Promise<bigint>} 铸造费用（以wei为单位）
 */
export const getMintFee = async () => {
  try {
    const fee = await publicClient.readContract({
      address: CONTRACT_ADDRESS,
      abi: NFT_ABI,
      functionName: 'mintFree'
    });
    return fee;
  } catch (error) {
    console.error('获取铸造费用失败:', error);
    throw error;
  }
};

/**
 * 铸造NFT - 修改为接受wallet客户端参数
 * @param {Object} options - 选项对象
 * @param {string} options.recipient - 接收者地址
 * @param {string} options.imageURI - 图片URI
 * @param {object} options.character - 角色元数据对象
 * @param {object} options.walletClient - 钱包客户端，通过AuthProvider获取
 * @returns {Promise<string>} 交易哈希
 */
export const mintNFT = async ({ recipient, imageURI, character, walletClient }) => {
  try {
    if (!walletClient) {
      throw new Error('缺少钱包客户端，请先连接钱包');
    }
    
    // 确保有账户
    if (!walletClient.account) {
      throw new Error('钱包未连接账户');
    }

    // 获取铸造费用
    const mintFee = await getMintFee();

    // 准备合约调用参数
    const metadata = JSON.stringify(buildCharacterMetadata(character, imageURI));
    const { request } = await publicClient.simulateContract({
      address: CONTRACT_ADDRESS,
      abi: NFT_ABI,
      functionName: 'mint',
      chain: unichainMainnet,
      args: [{
        recipient: recipient,
        courage: character.properties?.courage || 0,
        creativity: character.properties?.creativity || 0,
        decisiveness: character.properties?.decisiveness || 0,
        logic: character.properties?.logic || 0,
        morality: character.properties?.morality || 0,
        perception: character.properties?.perception || 0,
        wisdom: character.properties?.wisdom || 0,
        backstory: character.backstory || "",
        imageURI: imageURI,
        characterID: character.id,
        characterName: character.name
      }],
      account: walletClient.account,
      value: mintFee
    });

    // 发送交易
    const hash = await walletClient.writeContract({
      ...request,
      chain: unichainMainnet
    });
    return hash;
  } catch (error) {
    console.error('铸造NFT失败:', error);
    throw error;
  }
};

/**
 * 构建角色元数据
 * @param {object} character - 角色对象
 * @param {string} imageURI - 图片URI
 * @returns {object} 元数据对象
 */
const buildCharacterMetadata = (character, imageURI) => {
  const attributes = Object.entries(character.properties || {}).map(([trait_type, value]) => ({
    trait_type,
    value,
    display_type: 'number'
  }));

  return {
    name: character.name,
    description: character.backstory || `${character.name} - ${character.role}`,
    image: imageURI,
    attributes,
    properties: {
      role: character.role,
      profession: character.profession,
      behavior_pattern: character.behavior_pattern,
      trust_value: character.trust_value
    }
  };
};

/**
 * 获取用户拥有的NFT数量
 * @param {string} address - 用户钱包地址
 * @returns {Promise<number>} 拥有的NFT数量
 */
export const getUserNFTBalance = async (address) => {
  try {
    const balance = await publicClient.readContract({
      address: CONTRACT_ADDRESS,
      abi: NFT_ABI,
      functionName: 'balanceOf',
      args: [address]
    });
    
    return Number(balance);
  } catch (error) {
    // console.error('获取用户NFT数量失败:', error);
    throw error;
  }
};

/**
 * 获取合约的总供应量
 * @returns {Promise<number>} NFT总供应量
 */
export const getTotalSupply = async () => {
  try {
    const totalSupply = await publicClient.readContract({
      address: CONTRACT_ADDRESS,
      abi: NFT_ABI,
      functionName: 'totalSupply'
    });
    
    return Number(totalSupply);
  } catch (error) {
    // console.error('获取NFT总供应量失败:', error);
    throw error;
  }
};

/**
 * 获取NFT所有者
 * @param {number} tokenId - Token ID
 * @returns {Promise<string>} 所有者地址
 */
export const getOwnerOf = async (tokenId) => {
  try {
    const owner = await publicClient.readContract({
      address: CONTRACT_ADDRESS,
      abi: NFT_ABI,
      functionName: 'ownerOf',
      args: [tokenId]
    });
    
    return owner;
  } catch (error) {
    // 忽略非存在的tokenId错误
    if (error.message.includes('ERC721NonexistentToken')) {
      return null;
    }
    // console.error(`获取Token #${tokenId}所有者失败:`, error);
    throw error;
  }
};

/**
 * 获取NFT的URI和元数据
 * @param {number} tokenId - Token ID
 * @returns {Promise<object>} NFT元数据
 */
export const getNFTMetadata = async (tokenId) => {
  try {
    const tokenURI = await publicClient.readContract({
      address: CONTRACT_ADDRESS,
      abi: NFT_ABI,
      functionName: 'tokenURI',
      args: [tokenId]
    });
    
    // console.log(`获取到Token #${tokenId}的URI:`, tokenURI);
    
    // 解析base64编码的数据URI
    if (tokenURI.startsWith('data:application/json;base64,')) {
      const base64Data = tokenURI.replace('data:application/json;base64,', '');
      const jsonString = atob(base64Data);
      const metadata = JSON.parse(jsonString);
      // console.log(`解析后的Token #${tokenId}元数据:`, metadata);
      return {
        ...metadata,
        token_id: tokenId
      };
    }
    
    // 如果是IPFS或HTTP URI，需要进行获取
    const response = await fetch(tokenURI);
    const metadata = await response.json();
    // console.log(`通过HTTP获取的Token #${tokenId}元数据:`, metadata);
    return {
      ...metadata,
      token_id: tokenId
    };
  } catch (error) {
    // console.error(`获取Token #${tokenId}元数据失败:`, error);
    throw error;
  }
};

/**
 * 转换NFT元数据为AgentCard组件所需格式
 * @param {object} metadata - NFT元数据
 * @returns {object} 格式化的NFT数据
 */
export const formatNFTForAgentCard = (metadata) => {
  try {
    // 提取属性
    const attributeMap = {};
    if (metadata.attributes && Array.isArray(metadata.attributes)) {
      metadata.attributes.forEach(attr => {
        attributeMap[attr.trait_type.toLowerCase()] = attr.value;
      });
    }
    
    // 添加这个日志来查看原始image字段
    // console.log(`NFT图片URL:`, metadata.image);
    
    // 格式化返回对象
    const formattedNFT = {
      token_id: metadata.token_id,
      name: metadata.name || 'Unnamed NFT',
      description: metadata.description || '',
      // 直接将image URL赋值给svgString以便在卡片上显示
      svgString: metadata.image,
      imageUrl: metadata.image, // 保留原始图片URL
      // 属性格式化为AgentCard需要的数组格式
      attributes: [
        0, // classId (默认)
        0, // genderId (默认)
        attributeMap.courage || 0,
        attributeMap.wisdom || 0, 
        attributeMap.perception || 0,
        Math.floor(Math.random() * 100) // dna (随机值，因为原始数据中可能没有)
      ],
      agent: [
        attributeMap.logic || 0,
        attributeMap.creativity || 0,
        attributeMap.morality || 0,
        attributeMap.decisiveness || 0
      ]
    };
    
    // console.log('格式化后的NFT数据:', formattedNFT);
    return formattedNFT;
  } catch (error) {
    console.error('格式化NFT数据失败:', error);
    throw error;
  }
};

/**
 * 检查角色ID是否已经存在
 * @param {string} characterID - 角色ID
 * @returns {Promise<boolean>} 如果ID已存在返回true，否则返回false
 */
export const characterIDExists = async (characterID) => {
  try {
    const exists = await publicClient.readContract({
      address: CONTRACT_ADDRESS,
      abi: NFT_ABI,
      functionName: 'characterIDExits',
      args: [characterID]
    });
    
    return exists;
  } catch (error) {
    console.error(`检查角色ID ${characterID} 是否存在时出错:`, error);
    throw error;
  }
};

/**
 * 获取用户ETH余额
 * @param {string} address - 用户钱包地址
 * @returns {Promise<string>} 余额（以ETH为单位）
 */
export const getUserBalance = async (address) => {
  try {
    const balance = await publicClient.getBalance({
      address: address
    });
    
    // 转换为ETH单位，保留4位小数
    const balanceInEth = formatEther(balance);
    return Number(balanceInEth).toFixed(4);
  } catch (error) {
    console.error('获取用户余额失败:', error);
    throw error;
  }
};
